'use client';
import { Button } from '../../../components/button';
import type { HeaderProps } from './header.props';

import { useLanguage } from '@bells/core/i18n';
import { baseLocale, locales } from '@bells/i18n/runtime';
import languages from '@bells/iso/languages';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/dropdown-menu';
import { isEmpty } from 'lodash';
import { FlagIcon } from '../../../components/flag-icon';

export const LanguageSwitcher = ({
  locale,
  alternates,
}: Pick<HeaderProps, 'locale' | 'alternates'>) => {
  const { language } = useLanguage();
  const path = usePathname();

  const transformedLanguages = useMemo(() => {
    let transformedArray: {
      [key: string]: string;
    } = {};

    transformedArray = Object.fromEntries(Object.entries(alternates ?? {}));

    for (const lang of locales) {
      if (isEmpty(transformedArray[lang])) {
        transformedArray[lang] = lang == 'de' ? `/` : `/${lang}`;
      }
    }

    return Object.fromEntries(
      Object.entries(transformedArray).sort((a, b) => {
        if (a[0] === baseLocale) return -1;
        if (b[0] === baseLocale) return 1;
        return a[0].localeCompare(b[0]);
      })
    );
  }, [alternates, locale, path]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className='group/toggle inline-flex h-8 w-8 items-center justify-center gap-2 whitespace-nowrap rounded-md px-0 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'
          variant='ghost'
          type='button'
          size='icon'
        >
          {language == 'de' && (
            <FlagIcon
              alt={languages[language]?.names[language]}
              countryCode={'DE'}
              className='h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all'
            />
          )}
          {language == 'en' && (
            <FlagIcon
              alt={languages[language]?.names[language]}
              countryCode={'UK'}
              className='h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all'
            />
          )}

          <span className='sr-only'>Toggle language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        {Object.keys(transformedLanguages).map((lng) => {
          return (
            <DropdownMenuItem asChild onClick={() => {}} key={'switch' + lng}>
              <Link prefetch={false} href={transformedLanguages[lng] ?? ''}>
                {lng == 'de' && (
                  <FlagIcon
                    alt={languages[lng]?.names[lng]}
                    countryCode={'DE'}
                    className='h-[1.2rem] w-[1.2rem]'
                  />
                )}
                {lng == 'en' && (
                  <FlagIcon
                    alt={languages[lng]?.names[lng]}
                    countryCode={'GB'}
                    className='h-[1.2rem] w-[1.2rem]'
                  />
                )}
                {languages[lng]?.names[lng]}
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
